import { type ComponentPropsWithoutRef, useEffect, useState, useRef, useMemo } from 'react';
import Modal from '@/components/Modal';
import { BlockButton } from '@/components/BlockButton';
import useActiveUsers from '../../active_users/hooks/useActiveUsers';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import {
  createCandidatesInterviewLogsSchema,
  type CandidatesInterviewLogsSchema,
  type CandidatesInterviewLogsInput,
} from '../schemas/candidatesInterviewLogsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import SelectBox from '@/components/SelectBox';
import { showErrorToast, showSuccessToast } from '../../job_change_processes/lib/toast';
import useFetchCandidateInterviewLog from '../api/useFetchCandidateInterviewLog';
import useUpdateCandidateInterviewLog from '../api/useUpdateCandidateInterviewLog';
import CalendarDatetimeMenu from '../../job_change_processes/components/parts/CalendarDatetimeMenu';
import useOnClickOutside from '../../job_change_processes/hooks/useOnClickOutside';
import { datetimeFormat } from '@/utils/dateFormat';
import MultiSelect from './MultiSelect';
import type { SharedCustomField } from '@/types/serializers';
import CandidatesDatetimeFieldForForm from './CandidatesDatetimeFieldForForm';
import { buildCustomDataPayload } from '@/utils/customDataForm';

type Props = {
  modal: Omit<ComponentPropsWithoutRef<typeof Modal>, 'children'>;
  candidateProfileId: number;
  selectedInterviewLogId?: number;
  close: () => void;
  onSuccess: () => void;
  customFields?: Array<SharedCustomField>;
};

export default function CandidatesInterviewInfoUpdateModal({
  modal,
  candidateProfileId,
  close,
  onSuccess,
  selectedInterviewLogId = undefined,
  customFields = [],
}: Props) {
  const { data: selectedInterviewLog, refetch: selectedInterviewLogRefetch } =
    useFetchCandidateInterviewLog({
      candidateId: candidateProfileId,
      interviewLogId: selectedInterviewLogId ?? 0,
      enabled: selectedInterviewLogId !== undefined,
    });
  const { data: activeUsersData } = useActiveUsers();
  const { mutateAsync: updateInterviewLog } = useUpdateCandidateInterviewLog();
  const [isDateMenuOpen, setIsDateMenuOpen] = useState(false);
  const dateMenuRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(dateMenuRef, () => setIsDateMenuOpen(false));

  const onCloseDateMenu = () => {
    setIsDateMenuOpen(false);
  };

  const onSetDate = (date: Date) => {
    setValue('interviewedAt', date);
  };

  // カスタムフィールドに基づいてスキーマを動的に生成
  const schema = useMemo(
    () => createCandidatesInterviewLogsSchema(customFields || []),
    [customFields]
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { isValid, isSubmitting, errors },
  } = useForm<CandidatesInterviewLogsInput, unknown, CandidatesInterviewLogsSchema>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: {
      interviewedAt: new Date(),
      organizationUserId: '',
      customData: {},
    },
  });

  useEffect(() => {
    if (selectedInterviewLog) {
      const customData = selectedInterviewLog.custom_data.reduce(
        (acc: Record<string, string>, item) => {
          acc[String(item.custom_field.id)] = item.content;
          return acc;
        },
        {} as Record<string, string>
      );

      let interviewedDate: Date;
      try {
        const dateStr = selectedInterviewLog.interviewed_at;
        interviewedDate = new Date(dateStr);

        if (isNaN(interviewedDate.getTime())) {
          interviewedDate = new Date();
        }
      } catch (error) {
        interviewedDate = new Date();
      }

      reset({
        interviewedAt: interviewedDate,
        organizationUserId: String(selectedInterviewLog.organization_user.id || ''),
        customData,
      });
    }
  }, [selectedInterviewLogId, selectedInterviewLog, reset]);

  const onSubmit: SubmitHandler<CandidatesInterviewLogsSchema> = async (data) => {
    try {
      const custom_data = buildCustomDataPayload(data.customData, 'update');

      await updateInterviewLog({
        candidateProfileId: candidateProfileId,
        interviewLogId: selectedInterviewLog?.id ?? 0,
        title: selectedInterviewLog?.title ?? '面談',
        interviewed_at: data.interviewedAt,
        organization_user_id: Number(data.organizationUserId),
        custom_data: custom_data.length > 0 ? custom_data : undefined,
      });

      await selectedInterviewLogRefetch();
      showSuccessToast('面談情報を更新しました');
      reset();
      close();
      onSuccess();
    } catch (error) {
      showErrorToast('面談情報の更新に失敗しました');
    }
  };

  const activeUsersItems =
    activeUsersData?.activeUsers.map((user) => ({
      value: String(user.id),
      label: user.name,
    })) || [];

  const renderFieldInput = (field: SharedCustomField) => {
    const fieldId = `customData.${field.id}` as const;

    switch (field.field_type) {
      case 'long_text': {
        return <Textarea {...register(fieldId)} rows={6} className="p-3" />;
      }
      case 'date': {
        return (
          <input
            type="date"
            className="h-[42px] w-full rounded border border-solid border-border px-2 py-1 text-sm focus:border-2 focus:border-primary focus-visible:outline-hidden"
            {...register(fieldId)}
          />
        );
      }
      case 'datetime': {
        const currentValue = watch(fieldId) || '';
        return (
          <CandidatesDatetimeFieldForForm
            value={currentValue}
            onChange={(value: string) => {
              setValue(fieldId, value, { shouldValidate: true });
            }}
            placeholder="日時を選択"
          />
        );
      }
      case 'single_select': {
        const options =
          field.custom_field_options?.map((option) => ({
            value: option.value,
            label: option.value,
          })) || [];
        return <SelectBox {...register(fieldId)} options={options} />;
      }
      case 'multiple_select': {
        const options =
          field.custom_field_options && field.custom_field_options.length > 0
            ? field.custom_field_options.map((option) => ({
                id: option.id,
                value: option.value,
              }))
            : [];
        const currentValue = watch(fieldId) || '';
        return (
          <MultiSelect
            customFieldOptions={options}
            initialValues={currentValue}
            onChange={(selectedOptions: Array<{ id: number; value: string }>) => {
              const selectedValues = selectedOptions
                .map((option: { id: number; value: string }) => option.value)
                .join(',');
              setValue(fieldId, selectedValues, { shouldValidate: true });
            }}
          />
        );
      }
      case 'number': {
        return <TextField type="number" {...register(fieldId)} />;
      }
      default: {
        return <TextField type="text" {...register(fieldId)} />;
      }
    }
  };

  return (
    <Modal {...modal}>
      <form className="w-[880px] rounded bg-white" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex h-[56px] items-center border-b border-solid border-border px-6">
          <p className="text-base font-bold">面談情報</p>
        </div>
        <div className="hidden-scrollbar max-h-[441px] min-h-[436px] overflow-y-auto border-b border-solid border-border p-6">
          <div className="flex flex-col gap-6">
            <div className="flex gap-4">
              <div className="flex w-[216px] flex-col gap-2">
                <label htmlFor="interviewedAt" className="text-sm font-bold">
                  面談日時
                  <span className="ml-2 rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                    必須
                  </span>
                </label>
                <div className="relative" ref={dateMenuRef}>
                  <button
                    type="button"
                    className="flex h-[42px] w-full items-center rounded border border-solid border-border bg-white px-3 focus:border-2 focus:border-primary focus-visible:outline-hidden"
                    onClick={() => setIsDateMenuOpen(!isDateMenuOpen)}
                  >
                    <p className="text-sm">
                      {watch('interviewedAt')
                        ? datetimeFormat(watch('interviewedAt'))
                        : 'YYYY/MM/DD 00:00'}
                    </p>
                    <img
                      src="/agent/candidates/icon-schedule.svg"
                      width={24}
                      height={24}
                      alt=""
                      className="ml-auto"
                    />
                  </button>

                  {isDateMenuOpen && (
                    <div className="absolute z-[2]">
                      <CalendarDatetimeMenu
                        currentDate={watch('interviewedAt')}
                        onCloseDateMenu={onCloseDateMenu}
                        onSetDate={onSetDate}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex w-[216px] flex-col gap-2">
                <label htmlFor="organizationUserId" className="text-sm font-bold">
                  面談者
                  <span className="ml-2 rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                    必須
                  </span>
                </label>
                <SelectBox
                  id="organizationUserId"
                  {...register('organizationUserId')}
                  options={activeUsersItems}
                />
              </div>
            </div>

            {/* NOTE: カスタムフィールド */}
            <div className="">
              <div className="grid grid-cols-2 gap-6">
                {customFields.map((field) => {
                  const fieldError = errors.customData?.[String(field.id)];

                  return field.field_type === 'long_text' ? (
                    <div key={field.id} className="col-span-2 flex flex-col gap-2">
                      <label htmlFor={`customField_${field.id}`} className="text-sm font-bold">
                        {field.name}
                        {field.is_required && (
                          <span className="ml-2 rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                            必須
                          </span>
                        )}
                      </label>
                      {renderFieldInput(field)}
                      {fieldError && (
                        <p className="mt-1 text-xs text-red-500">
                          {typeof fieldError === 'string' ? fieldError : fieldError.message}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div key={field.id} className="flex flex-col gap-2">
                      <label htmlFor={`customField_${field.id}`} className="text-sm font-bold">
                        {field.name}
                        {field.is_required && (
                          <span className="ml-2 rounded-full bg-danger_lighten px-3 py-1 text-xs font-bold text-danger">
                            必須
                          </span>
                        )}
                      </label>
                      {renderFieldInput(field)}
                      {fieldError && (
                        <p className="mt-1 text-xs text-red-500">
                          {typeof fieldError === 'string' ? fieldError : fieldError.message}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        <div className="flex h-[74px] items-center justify-end gap-4 px-6">
          <div className="w-[52px]">
            <BlockButton type="button" colorType="white" text="戻る" onClick={close} />
          </div>
          <div className="w-[80px]">
            <BlockButton type="submit" text="保存する" disabled={isSubmitting || !isValid} />
          </div>
        </div>
      </form>
    </Modal>
  );
}
